/**
 * AddProgramAction — V98-3-1 / V98-3-2. Starts an absent program
 * (POST /api/w/:slug/certification-profiles — owner/admin; the server
 * enforces it). Members who can't start one get AskAdmin, so the empty
 * state always offers a next step (no dead ends).
 *
 * V108-8-15: a program with key fields in its manifest (SOC 2 type, FedRAMP class, GovRAMP impact level + tier,
 * ISO 42001 body + cycle start) asks them in one short step first: defaults preselected, optional fields blank,
 * one Add. Nothing else to answer, so it is as fast as the old one-click add.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button } from '@aigovops/design-system';
import { createProgram } from '../../lib/api';
import { invalidationMap } from '../../lib/query-keys';
import { AskAdmin } from '../AskAdmin';
import { getProgramManifest, programAddFields, programCreateFields, type ProgramKey, type ProgramManifest } from '@aigovops/core';
import { programTitle } from '../../lib/nav-config';
import { defaultFieldValues, ProgramFieldInputs } from './ProgramFields';

type CreateProgramBody = Parameters<typeof createProgram>[1];

/** The commit label (manifest `onboarding.addProgramLabel`) — what will actually be created. */
export function addProgramLabel(m: ProgramManifest): string {
  return m.onboarding.addProgramLabel;
}

/**
 * The create body: the program plus its manifest `onboarding.addProgramDefaults` — profile columns as
 * columns, the rest as `program_options` (GovRAMP tier + impact level, V103-3-7).
 */
export function addProgramBody(m: ProgramManifest, chosen: Readonly<Record<string, string>> = {}): CreateProgramBody {
  return { ...(programCreateFields(m, chosen) as Partial<CreateProgramBody>), program: m.key as ProgramKey };
}

export function AddProgramAction({ slug, program, manifest, canManage, variant = 'primary', onAdded }: {
  slug: string;
  program: string;
  /** The program's manifest (defaults to the registry entry for `program`). */
  manifest?: ProgramManifest;
  canManage: boolean;
  variant?: 'primary' | 'secondary';
  onAdded?: () => void;
}) {
  const queryClient = useQueryClient();
  const uid = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});
  const openRef = useRef<HTMLButtonElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  // A11y: the step opens with focus on its first field (never left on a button that is gone).
  useEffect(() => { if (asking) formRef.current?.querySelector<HTMLElement>('select, input')?.focus(); }, [asking]);
  const m = manifest ?? getProgramManifest(program);
  if (!m) return null;
  const label = addProgramLabel(m);
  if (!canManage) {
    return <AskAdmin slug={slug} what="starts programs" subject={`GAC Pro: please ${label.toLowerCase()}`} />;
  }
  // Flow M (review F-11): ask first only when there is a choice to make; optional fields (ISO body / cycle start) are set
  // later from the program page, so adding stays one click.
  const hasFields = programAddFields(m).some((f) => !f.optional);
  const add = async (chosen: Record<string, string> = {}) => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      await createProgram(slug, addProgramBody(m, chosen));
      await Promise.all(invalidationMap['program:changed'](slug).map((queryKey) => queryClient.invalidateQueries({ queryKey })));
      setAsking(false);
      onAdded?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : `${programTitle(m)} could not be started. Try again.`);
    } finally {
      setBusy(false);
    }
  };
  if (hasFields && asking) {
    return (
      // Aesthetic M (review A-8): inline in the program's own card or row, no card-in-card and no repeated title.
      <form ref={formRef} className="gp-form gp-fields-form gp-fields-form--inline" aria-label={`Set up ${programTitle(m)}`} data-part="add-program-fields" onSubmit={(e) => { e.preventDefault(); void add(values); }}>
        <ProgramFieldInputs m={m} values={values} onChange={setValues} idPrefix={uid} />
        {error ? <Alert variant="error">{error}</Alert> : null}
        <div className="gp-actions">
          <Button type="submit" variant="primary" aria-disabled={busy || undefined}>{busy ? 'Starting…' : `Add ${m.shortLabel}`}</Button>
          <Button type="button" variant="secondary" onClick={() => { setAsking(false); window.setTimeout(() => openRef.current?.focus(), 0); }}>Cancel</Button>
        </div>
      </form>
    );
  }
  return (
    <>
      <Button ref={openRef} variant={variant} aria-disabled={busy || undefined}
        onClick={() => { if (hasFields) { setValues(defaultFieldValues(m)); setError(null); setAsking(true); } else void add(); }}>
        {busy ? 'Starting…' : label}
      </Button>
      {error ? <Alert variant="error">{error}</Alert> : null}
    </>
  );
}
